import React, {useEffect, useState} from 'react';
import { BodyWrapper } from '../components/BodyWrapper';
import { TerminalFrame } from '../components/TerminalFrame';
import { PromptField } from '../components/PromptField';
import { PromptSelect } from '../components/PromptSelect';
import { TerminalButton } from '../components/TerminalButton';
import { setHomeTitle } from '../utils/title';
import { pingPageView } from '../utils/analytics';
import { FileAttach } from '../components/FileAttach';
import { createPad, uploadPadFiles } from '../utils/fetch';
import { Colors, Fonts } from '../utils/colors';
import { Redirect } from 'react-router-dom';
import { CreatePadResponse } from '../models/CreatePad';
import styled from 'styled-components';

export const HomePage = () => {
  const [createdPadName, setCreatedPadName] = useState<string | null>(null)
  const [isCreateInProgress, setIsCreateInProgress] = useState(false)
  const [expirationInMinutes, setExpirationInMinutes] = useState(60)
  const [passwordEnabled, setPasswordEnabled] = useState(false)
  const [password, setPassword] = useState("")
  const [initialNote, setInitialNote] = useState("")
  const [initialFiles, setInitialFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadLabel, setUploadLabel] = useState<string | null>(null)

  useEffect(() => {
    setHomeTitle()
    pingPageView()
  }, [])

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(event.target.value)
  }

  const handleInitialNoteChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInitialNote(event.target.value)
  }

  const padPassword = passwordEnabled ? password : ""
  const canMint = !passwordEnabled || password.trim().length > 0

  const handleCreateSubmit = () => {
    if (!canMint || isCreateInProgress) {
      return
    }
    const sendCreatePadCall = async () => {
      try {
        setIsCreateInProgress(true)
        setUploadProgress(null)
        setUploadLabel(null)
        const response: CreatePadResponse =
          await createPad(expirationInMinutes, padPassword, initialNote)
        if (initialFiles.length > 0) {
          await uploadPadFiles(
            response.name,
            initialFiles,
            padPassword,
            (update) => {
              setUploadProgress(update.percent)
              setUploadLabel(
                `uploading ${update.current}/${update.total}: ${update.fileName}`
              )
            },
          )
        }
        setCreatedPadName(response.name)
      } catch (error) {
        window.alert((error as Error).message)
        setIsCreateInProgress(false)
        setUploadProgress(null)
        setUploadLabel(null)
      }
    }
    sendCreatePadCall()
  }

  if (createdPadName) {
    return (
      <Redirect
        to={{
          pathname: "/" + createdPadName,
          state: { password: padPassword },
        }}
      />
    )
  }

  return (
    <BodyWrapper>
      <Page>
        <TerminalFrame title="mint a pad">
          <Fields>
            <PromptSelect
              label="expiration"
              value={expirationInMinutes}
              onChange={setExpirationInMinutes}
              options={[
                {value: 10, label: "10 minutes"},
                {value: 60, label: "1 hour"},
                {value: 360, label: "6 hours"},
                {value: 1440, label: "24 hours"},
              ]}
            />
            <PromptField
              label="note"
              placeholder="a super secret code"
              multiline
              rows={4}
              value={initialNote}
              onChange={handleInitialNoteChange}
            />
            <FileAttach
              files={initialFiles}
              onChange={setInitialFiles}
              disabled={isCreateInProgress}
              uploadProgress={uploadProgress}
              uploadLabel={uploadLabel}
            />
            <ToggleRow>
              <ToggleButton
                type="button"
                $on={passwordEnabled}
                onClick={() => setPasswordEnabled(!passwordEnabled)}
              >
                [{passwordEnabled ? "x" : " "}] protect with a password
              </ToggleButton>
            </ToggleRow>
            {passwordEnabled && (
              <PromptField
                label="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
              />
            )}
            <TerminalButton
              type="button"
              disabled={isCreateInProgress || !canMint}
              onClick={handleCreateSubmit}
            >
              mint
            </TerminalButton>
          </Fields>
        </TerminalFrame>
        <TerminalFrame title="how it works">
          <Explainer>
            A pad is an anonymous and secure self-destructing collection of notes
            and files. Think credentials, zips, or credit card numbers. Share the
            link with someone so they can read it or add their own notes and
            files. When the timer expires, the pad is destroyed.
          </Explainer>
        </TerminalFrame>
        <Footer>
          © {new Date().getFullYear()} NodeHeap LLC. All rights reserved.
        </Footer>
      </Page>
    </BodyWrapper>
  )
}

const Page = styled.div`
  max-width: 520px;
  min-height: calc(100vh - 96px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: ${Fonts.mono};
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ToggleRow = styled.div``;

const ToggleButton = styled.button<{$on: boolean}>`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  color: ${props => props.$on ? Colors.destructGreen : Colors.terminalMuted};
  font-family: ${Fonts.mono};
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: color 0.15s ease, text-shadow 0.15s ease;

  &:hover,
  &:focus-visible {
    color: ${Colors.destructGreen};
    text-shadow: 0 0 8px ${Colors.terminalGlow};
    outline: none;
  }
`;

const Explainer = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: ${Colors.terminalMuted};
`;

const Footer = styled.p`
  margin: auto 0 16px;
  text-align: center;
  font-size: 12px;
  color: ${Colors.terminalDim};
`;
